<template>
    <ContentComp>
        <p v-html="$tm('countrymapper.info', '', true)"></p>

        <FormLayout>
            <div class="d-flex flex-wrap h-100">
                <div
                    class="w-50 w-md-50 w-sm-100 w-xs-100 px-3"
                    v-for="(val, index) in sourceValues"
                    :key="val"
                >
                    <div class="d-flex flex-nowrap gap-2">
                        <div class="ms-3 w-50 align-self-center">
                            {{ val }}
                        </div>
                        <IconComp
                            icon="fa-arrow-right"
                            class="me-2 opacity-50 align-self-center"
                        />
                        <div class="flex-fill d-flex align-self-center">
                            <SelectCountry v-model="selectedValues[index]" />
                        </div>
                    </div>
                </div>
            </div>
        </FormLayout>
    </ContentComp>
</template>

<script setup lang="ts">
import { ContentComp, SelectCountry } from '@/components';
import { FormLayout } from '../Form';
import { watch, ref, computed } from 'vue';
import IconComp from '../IconComp.vue';
import type { Country } from '@/objects/generated';
import { useCommonStore } from '@/stores';
import { $tm } from '@/locize';

const props = defineProps<{
    /**
     * The model value is a map of the source values and the selected country
     */
    modelValue: Map<string, Country> | null;

    /**
     * The source values to map. Ie the values that we want to map to a country
     */
    sourceValues: string[];
}>();

// A reference to the glbal countries cache
const { Countries } = useCommonStore();

// Emit the model value
const emit = defineEmits(['update:modelValue']);

// The internal writeable value
const value = computed({
    get: () => props.modelValue,
    set: (value: Map<string, Country> | null) => emit('update:modelValue', value),
});

// The selected values for the dropdowns
const selectedValues = ref<number[]>([]);

// fill up the selectedValues with an array of 160 (norway) of the same length as sourceValues
// TODO: This should probably default to the country of the event or federation
selectedValues.value = Array(props.sourceValues.length).fill(160);

// We try and map the selected values to the countries based on the current selected values
for (let i = 0; i < props.sourceValues?.length; i++) {
    const sourceCountry = props.sourceValues[i]?.toUpperCase();

    // First we try with the ISO2, ISO3, FIFA, IOC and Name
    let sourceCountryId: number | undefined = undefined;

    if (sourceCountry.length == 3) {
        // We assume that it is a IOC code
        sourceCountryId = Countries.find((c) => c.IOC == sourceCountry)?.ID;

        // If we did not find it, we try with the ISO3
        if (!sourceCountryId) {
            sourceCountryId = Countries.find((c) => c.ISO3 == sourceCountry)?.ID;
        }

        // If we did not find it, we try with the FIFA
        if (!sourceCountryId) {
            sourceCountryId = Countries.find((c) => c.FIFA == sourceCountry)?.ID;
        }
    }

    // At the very last we try with the Name and the translated name
    if (!sourceCountryId) {
        sourceCountryId = Countries.find(
            (c) =>
                c.ISO2 == sourceCountry ||
                c.Name?.toUpperCase().startsWith(sourceCountry) ||
                $tm(c.TranslationKey ?? '')
                    .toUpperCase()
                    .startsWith(sourceCountry),
        )?.ID;
    }

    selectedValues.value[i] = sourceCountryId || 160;
}

watch(
    () => selectedValues.value,
    (newVal) => {
        const newMap = new Map<string, Country>();
        for (let i = 0; i < props.sourceValues.length; i++) {
            const country = Countries.find((c) => c.ID === newVal[i]);

            if (country) {
                newMap.set(props.sourceValues[i].toUpperCase(), country);
            }
        }
        value.value = newMap;
    },
    { deep: true, immediate: true },
);
</script>
